import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

export default function BarcodeScanner({ isOpen, onClose, onScan }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const isScanning = useRef(true);

  useEffect(() => {
    if (!isOpen) return;
    isScanning.current = true;

    let stream = null;
    let scanInterval = null;

    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("L'accès caméra est bloqué (nécessite HTTPS ou le flag Chrome).");
        }

        // 1. Détection des caméras disponibles
        let selectedDeviceId = undefined;
        try {
          const devices = await BrowserMultiFormatReader.listVideoInputDevices();
          if (devices.length > 0) {
            const targetIndex = devices.length >= 4 ? 3 : devices.length - 1;
            selectedDeviceId = devices[targetIndex]?.deviceId;
          }
        } catch (e) {
          console.warn("Impossible de lister les caméras, repli sur le mode standard.");
        }

        // 2. Tentative avec contraintes optimisées, puis secours si le téléphone refuse
        const primaryConstraints = selectedDeviceId
          ? { video: { deviceId: { exact: selectedDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } } }
          : { video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } } };

        try {
          stream = await navigator.mediaDevices.getUserMedia(primaryConstraints);
        } catch (errConstraint) {
          console.warn("Échec contraintes strictes, tentative standard :", errConstraint);
          // Secours universel
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" }
          });
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          await videoRef.current.play();
        }

        // 3. Préparation des décodeurs
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.DATA_MATRIX]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const zxingReader = new BrowserMultiFormatReader(hints);

        let detector = null;
        if ('BarcodeDetector' in window) {
          try {
            const formats = await window.BarcodeDetector.getSupportedFormats();
            if (formats.includes('data_matrix')) {
              detector = new window.BarcodeDetector({ formats: ['data_matrix'] });
            }
          } catch (e) {}
        }

        // 4. Boucle de lecture avec inversion alternée
        let frameCount = 0;
        let isDecoding = false;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        scanInterval = setInterval(async () => {
          if (!isScanning.current || !videoRef.current || videoRef.current.readyState < 2 || isDecoding) return;

          isDecoding = true;
          try {
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;

            frameCount++;
            ctx.filter = (frameCount % 2 === 0) ? 'invert(1)' : 'none';
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

            if (detector) {
              const barcodes = await detector.detect(canvas);
              if (barcodes.length > 0 && isScanning.current) {
                isScanning.current = false;
                onScan(barcodes[0].rawValue);
                return;
              }
            }

            const result = await zxingReader.decodeFromCanvas(canvas);
            if (result && isScanning.current) {
              isScanning.current = false;
              onScan(result.getText());
            }
          } catch (e) {
          } finally {
            isDecoding = false;
          }
        }, 60);

      } catch (err) {
        console.error("Erreur caméra :", err);
        setErrorMessage(err.message || "Impossible d'accéder à la caméra.");
      }
    };

    startCamera();

    return () => {
      isScanning.current = false;
      if (scanInterval) clearInterval(scanInterval);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen, onScan]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black">
      <canvas ref={canvasRef} className="hidden" />

      {errorMessage ? (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
          <p className="text-red-400 font-medium">{errorMessage}</p>
        </div>
      ) : (
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" />
      )}

      <div className="absolute inset-0 flex flex-col justify-between p-4 pointer-events-none">
        <div className="flex justify-between items-start pointer-events-auto pt-2 mt-8">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-12 h-12 bg-black/50 text-white rounded-full backdrop-blur-md border border-white/20 shadow-lg active:bg-black/70 transition"
            aria-label="Fermer la caméra"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}